import type { CrmCommentRow, CrmRuleView, CrmRunView } from '@src/components/sco/Admin/AiCrm/data/types'

/**
 * The member message a draft or send answers, or null when the reply is a follow-up.
 *
 * The server stamps the run for the inbound it is given: the draft or the send becomes that run's
 * outcome. That is right for the first reply to an unanswered message, and wrong for a follow-up,
 * which would overwrite what an automatic or earlier reply already recorded for the run. So the id is
 * passed only when nothing has gone out since the last member message. `followUp` is the operator's
 * own "Write a follow-up".
 */
export function inboundForReply(input: {
  comments: Array<Pick<CrmCommentRow, 'commentId' | 'direction'>>
  followUp?: boolean
  run?: Pick<CrmRunView, 'inboundCommentId' | 'outcomeKey'> | null
}): string | null {
  if (input.followUp) {
    return null
  }
  let lastInbound = -1
  input.comments.forEach((comment, index) => {
    if (comment.direction === 'in') {
      lastInbound = index
    }
  })
  if (lastInbound < 0) {
    return null
  }
  const inboundCommentId = input.comments[lastInbound].commentId
  // A reply that was sent and then unsent still counts: the run already carries it.
  const answered = input.comments.slice(lastInbound + 1).some(comment => comment.direction === 'out')
  // The latest run may belong to an earlier message. Only a send recorded for this one counts.
  const run = input.run
  const runSent =
    run?.outcomeKey === 'sent' && (!run.inboundCommentId || run.inboundCommentId === inboundCommentId)
  if (answered || runSent) {
    return null
  }
  return inboundCommentId
}

/**
 * "Drafted by AI" is true only for the text the rules wrote. An operator's own saved draft is a tool
 * draft too, so the origin alone cannot say. The run holds the rules' suggestion: the label shows
 * while an open draft still reads exactly like it.
 */
export function isAiDraft(input: {
  body: string
  draftId: string | null
  suggestedBody: string | null | undefined
}): boolean {
  const suggested = (input.suggestedBody || '').trim()
  return Boolean(input.draftId) && suggested.length > 0 && input.body.trim() === suggested
}

const OPENABLE_OUTCOMES = ['held', 'drafted', 'shadow']

export type ComposerDraft = {
  body: string
  draftId: string | null
  /** True when the open text is still the rules' suggestion, so the label can say "Drafted by AI". */
  fromRules: boolean
}

/**
 * What the conversation composer opens with. A saved tool draft wins. Otherwise a held, drafted,
 * or shadow run's suggested body is the draft, and only while that member message is still
 * unanswered. A sent or silent run does not put its old suggestion back in the box.
 */
export function composerDraft(input: {
  comments: Array<{ commentId: string; direction: 'in' | 'out' }>
  drafts: Array<{ commentId: string; body: string }>
  followUp?: boolean
  run?: {
    suggestedBody?: string | null
    outcomeKey?: string | null
    inboundCommentId?: string | null
    suggestionSourceKey?: 'stored-template' | 'operator-packet' | 'incomplete' | null
  } | null
}): ComposerDraft | null {
  if (input.followUp) {
    return null
  }
  const briefing =
    input.run && 'suggestionSourceKey' in input.run && input.run.suggestionSourceKey === 'operator-packet'
  const suggested = briefing ? '' : (input.run?.suggestedBody || '').trim()
  const saved = input.drafts.find(draft => draft.body.trim())
  if (saved) {
    return {
      body: saved.body,
      draftId: saved.commentId,
      fromRules: suggested.length > 0 && saved.body.trim() === suggested,
    }
  }
  const outcome = input.run?.outcomeKey
  if (!suggested || !outcome || !OPENABLE_OUTCOMES.includes(outcome)) {
    return null
  }
  if (!inboundForReply({ comments: input.comments, run: input.run })) {
    return null
  }
  return { body: suggested, draftId: null, fromRules: true }
}

/**
 * Who else is working this reply, when that should freeze the composer.
 *
 * A claim only protects an open reply (a saved draft or the rules' suggestion still in the box).
 * An empty box, including one just discarded, stays editable so the next person can write.
 */
export function composerLockedBy(input: {
  ownerDisplayName?: string | null
  ownedByViewer?: boolean | null
  replyOpen: boolean
}): string | null {
  if (!input.replyOpen || input.ownedByViewer !== false) {
    return null
  }
  const name = (input.ownerDisplayName || '').trim()
  return name || null
}

/** "Send as Ed" asks first when the visible author is a real person, because the member sees that person's name. */
export function sendNeedsConfirm(
  sendAs: { name: string | null; person: boolean } | null | undefined
): boolean {
  return Boolean(sendAs?.person && sendAs.name)
}

/**
 * Reply text that was typed and not sent, per conversation, for as long as the desk stays open. It is
 * component state, never storage: a closed tab forgets it. An entry also remembers which draft it was
 * typed over, so text typed over one draft does not replace a different one that arrived later.
 */
export type UnsavedReply = { body: string; draftId: string | null }

export function createUnsavedReplies() {
  const entries = new Map<string, UnsavedReply>()
  return {
    clear(threadId: string) {
      entries.delete(threadId)
    },
    /** The remembered text for a conversation, only if it was typed over the same draft. */
    read(threadId: string, draftId: string | null): UnsavedReply | null {
      const found = entries.get(threadId)
      return found && found.draftId === draftId ? found : null
    },
    write(threadId: string, entry: UnsavedReply) {
      if (entry.body.trim()) {
        entries.set(threadId, entry)
      } else {
        entries.delete(threadId)
      }
    },
  }
}

export type UnsavedReplies = ReturnType<typeof createUnsavedReplies>

/** One rule a person can draft the reply from. */
export type RedraftChoice = {
  toolRuleId: string
  name: string
  /** Hard stops are the "human needed" rules. Lanes are the judgement calls. */
  group: 'hard-stop' | 'lane'
}

/**
 * The rules the Regenerate and Change rule controls can use: on for this account, and with wording to
 * draft from. A rule with no stored wording is left out, since the server refuses it.
 */
export function redraftChoices(
  library: { hardStops: CrmRuleView[]; lanes: CrmRuleView[] } | null | undefined
): RedraftChoice[] {
  if (!library) {
    return []
  }
  return [...library.hardStops, ...library.lanes]
    .filter(view => view.enabled && view.effectiveTemplate.trim().length > 0)
    .map(view => ({ toolRuleId: view.rule.toolRuleId, name: view.rule.name, group: view.rule.groupKey }))
}

/**
 * Whether the reply can be written again: a person can still use the wording, so the message is the
 * member's latest, nothing has gone out for it, nobody cleared it, and support does not have it. The
 * server checks every one of these again.
 */
export function canRedraft(input: {
  comments: Array<Pick<CrmCommentRow, 'commentId' | 'direction'>>
  followUp?: boolean
  latestInboundCommentId?: string | null
  locked?: boolean
  run?: Pick<CrmRunView, 'dismissedAt' | 'inboundCommentId' | 'outcomeKey'> | null
  supportOpen?: boolean
}): boolean {
  const run = input.run
  if (input.followUp || input.locked || input.supportOpen || !run || run.dismissedAt) {
    return false
  }
  if (!OPENABLE_OUTCOMES.includes(run.outcomeKey)) {
    return false
  }
  // The run has to be for the message the screen is showing, or the server refuses the stale one.
  if (!input.latestInboundCommentId || run.inboundCommentId !== input.latestInboundCommentId) {
    return false
  }
  return inboundForReply({ comments: input.comments, run }) != null
}

/**
 * Text as a person reads it, for comparing the box with what the rules wrote. The editor writes the
 * markdown back out with its own line breaks and escapes, so an untouched draft does not come back
 * byte for byte.
 */
function readable(text: string): string {
  return text
    .replace(/\\([\\`*_{}[\]()#+\-.!>~|$])/g, '$1')
    .replace(/\s+/g, ' ')
    .trim()
}

export type GenerateScope = 'last' | 'thread'

type GenerateComment = {
  /** out = the account. Files on those messages are not sent to the model. */
  direction?: 'in' | 'out'
  files?: Array<unknown> | null
  isDraft?: boolean
  withdrawnAt?: string | null
}

/** Sent messages the Generate box can offer. Drafts and unsent messages stay out. */
export function commentsForGenerate<T extends GenerateComment>(comments: T[], scope: GenerateScope): T[] {
  const sent = comments.filter(comment => !comment.isDraft && !comment.withdrawnAt)
  return scope === 'last' ? sent.slice(-1) : sent
}

/** Files on member messages Generate would send for this scope. The account's own files stay out. */
export function generateFileCount(comments: GenerateComment[], scope: GenerateScope): number {
  return commentsForGenerate(comments, scope).reduce((count, comment) => {
    if (comment.direction === 'out') {
      return count
    }
    return count + (comment.files?.length || 0)
  }, 0)
}

/**
 * Writing the reply again replaces the box. That is only worth a question when the box holds something
 * the rules did not write: anything typed over the suggestion, saved or not.
 */
export function redraftNeedsConfirm(input: {
  body: string
  suggestedBody: string | null | undefined
}): boolean {
  const typed = readable(input.body)
  return typed.length > 0 && typed !== readable(input.suggestedBody || '')
}
